import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { DataTable } from "@/components/data-table";
import { useGlobalLoader } from "@/components/GlobalLoader";
import { useImpressao } from "@/components/ImpressaoComponent";
import { MODOS_IMPRESSAO } from "@/lib/modosImpressao";
import { indicesInfoCompl, TOTAL_INFO_COMPL_MAXIMO } from "@/lib/infoCompl";
import { ehDestinoInovaid } from "@/lib/destinoItens";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { aplicarPreDefinicoesUnicas } from "@/components/InfoComplField";
import { AdminContext } from "@/contexts/AdminContext";
import { useCategorias } from "@/contexts/CategoriasContext";
import { useContaConfig } from "@/contexts/ContaConfigContext";
import { useEpc } from "@/contexts/EpcContext";
import { useImpressaoConfig } from "@/contexts/ImpressaoConfigContext";
import { useItem } from "@/contexts/ItemContext";
import { useLocalizacoes } from "@/contexts/LocalizacoesContext";
import { useSeriais } from "@/contexts/SeriaisContext";
import type { CategoriaItemResponse } from "@/types/categoria-item-response";
import type { ImpressaoItem } from "@/types/impressao-item";
import type { LocalizacaoItem } from "@/types/localizacao-nv1-item";
import type { LocalizacaoNv2Item } from "@/types/localizacao-nv2-item";
import type { LocalizacaoNv3Item } from "@/types/localizacao-nv3-item";
import type { LocalizacaoNv4Item } from "@/types/localizacao-nv4-item";
import type { NewCategoriaItem } from "@/types/new-categoria-item";
import type { newLocalizacaoNv1 } from "@/types/new-localizacao-nv1";
import type { newLocalizacaoNv2 } from "@/types/new-localizacao-nv2";
import type { newLocalizacaoNv3 } from "@/types/new-localizacao-nv3";
import type { newLocalizacaoNv4 } from "@/types/new-localizacao-nv4";
import type { PlanilhaItem } from "@/types/planilha-item";
import {
  brDateToISO,
  sanitizarCodigoHex,
  verificarEpcsInvalidos,
  verificarVariaveisDeSubstituicaoEmInfosComplementares,
} from "@/utils/UtilFuncoes";
import type { ColumnDef } from "@tanstack/react-table";
import { alert } from "@/contexts/AlertModalContext";
import {
  AlertTriangle,
  Download,
  Eye,
  FileSpreadsheet,
  Layers,
  Printer,
  RefreshCw,
  Upload,
  X,
} from "lucide-react";

const link_exemplo_planilha_item_com_epc =
  "https://inovacodefirmup.s3.sa-east-1.amazonaws.com/Outros/exemplo_planilha_item_com_epc.csv";

const link_exemplo_planilha_item_sem_epc =
  "https://inovacodefirmup.s3.sa-east-1.amazonaws.com/Outros/exemplo_planilha_item_sem_epc.csv";

// ---- Colunas conhecidas e ordem dos modelos oficiais ----
// 11 e 12 no fim, para não deslocar planilhas sem cabeçalho no modelo antigo
const COLUNAS_INF_COMPL = Array.from(
  { length: TOTAL_INFO_COMPL_MAXIMO },
  (_, i) => `INF_COMPL_${i + 1}`,
);

const COLUNAS_MODELO_COM_EPC = [
  "TAG",
  "EAN",
  "DESCRICAO",
  "QUANTIDADE",
  "VALOR",
  "DT_VALIDADE",
  "LOCALIZACAO_1",
  "LOCALIZACAO_2",
  "LOCALIZACAO_3",
  "LOCALIZACAO_4",
  ...COLUNAS_INF_COMPL,
];

const COLUNAS_MODELO_SEM_EPC = COLUNAS_MODELO_COM_EPC.filter(
  (coluna) => coluna !== "TAG",
);

const COLUNAS_CONHECIDAS = COLUNAS_MODELO_COM_EPC;

const CAMPOS_SEMPRE_OBRIGATORIOS: Array<keyof PlanilhaItem> = [
  "EAN",
  "DESCRICAO",
];

const MAX_LINHAS_POR_CAMPO_NO_ERRO = 5;

// Blocos do EPC personalizado que puxam valor da planilha. Os demais blocos
// (serial, data, constantes) nao dependem do arquivo importado.
const CAMPO_DA_PLANILHA_POR_BLOCO_EPC: Record<string, keyof PlanilhaItem> = {
  "{codigo}": "EAN",
  "{codigosze}": "EAN",
  "{desc}": "DESCRICAO",
  ...Object.fromEntries(
    COLUNAS_INF_COMPL.map((coluna, i) => [`{info${i + 1}}`, coluna]),
  ),
};

function ImportarPlanilha() {
  const { t } = useTranslation();
  const { setIsLoading } = useGlobalLoader();
  const { numeroDeImpressoes } = useContaConfig();
  const { isLoading } = useGlobalLoader();
  const { contaConfig } = useContaConfig();
  const { impressaoConfig, coletarBlocosEPC, coletarPadraoEpc } =
    useImpressaoConfig();
  // Info. compl. 11 e 12 da planilha só valem com destino iNOVAID
  const infoExtraDaPlanilha = (valor?: string) =>
    ehDestinoInovaid(impressaoConfig) ? valor || "" : "";
  const {
    coletaCategorias,
    criarCategorias,
    pesquisarCategoria,
    atualizarDescricaoCategoria,
  } = useCategorias();
  const { coletaSeriais, seriais, adicionarMaisUmNosSeriais } = useSeriais();
  const {
    coletaLocalizacoes,
    localizacoes,
    criarLocalizacoesNV1,
    criarLocalizacoesNV2,
    criarLocalizacoesNV3,
    criarLocalizacoesNV4,
  } = useLocalizacoes();
  const { gerarEpcs } = useEpc();
  const { conferirTagsCadastradas } = useItem();
  const { adicionarItensNaFila } = useImpressao();
  const { requireAdmin } = useContext(AdminContext)!;

  const [planilhaDeItens, setPlanilhaDeItens] = useState<PlanilhaItem[]>([]);
  const [piscarBotaoExemplo, setPiscarBotaoExemplo] = useState(false);
  const [permitirItensRepetidos, setPermitirItensRepetidos] = useState(false);
  const [epcRepetidosDialog, setEpcRepetidosDialog] = useState(false);
  const [epcRepetidosEncontrados, setEpcRepetidosEncontrados] = useState<
    Array<{ epc: string; codigo: string }>
  >([]);
  const [codigosConvertidos, setCodigosConvertidos] = useState<
    Array<{ original: string; convertido: string }>
  >([]);
  const [cabecalhoAssumido, setCabecalhoAssumido] = useState<string[] | null>(
    null,
  );
  const [fileName, setFileName] = useState<string>("");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ---- Preferência persistida (chave consistente) ----
  useEffect(() => {
    const checked = localStorage.getItem("permitirItensRepetidos");
    setPermitirItensRepetidos(checked === "true");
  }, []);

  // ---- Pisca o botão de exemplo se houver problema na planilha ----
  useEffect(() => {
    if (piscarBotaoExemplo) {
      const timer = setTimeout(() => setPiscarBotaoExemplo(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [piscarBotaoExemplo]);

  // ---- Colunas da tabela ----
  const baseColumns: ColumnDef<PlanilhaItem>[] = [
    {
      accessorKey: "EAN",
      header: "EAN",
      cell: ({ row }) => {
        const original = row.original.EAN;
        const convertido = sanitizarCodigoHex(original);
        const foiConvertido = convertido !== original.toUpperCase();
        return (
          <div className="flex items-center gap-2">
            <span className="text-xs">{convertido}</span>
            {foiConvertido && (
              <Badge
                variant="outline"
                className="text-[10px] text-amber-700 dark:text-amber-400 border-amber-500/30 bg-amber-500/10 gap-1"
              >
                <AlertTriangle className="h-3 w-3" />
                {original}
              </Badge>
            )}
          </div>
        );
      },
    },
    { accessorKey: "DESCRICAO", header: t("planilha.description") },
    { accessorKey: "VALOR", header: t("planilha.value") },
    { accessorKey: "QUANTIDADE", header: t("planilha.quantity") },
    { accessorKey: "DT_VALIDADE", header: t("planilha.expiry") },
    { accessorKey: "LOCALIZACAO_1", header: t("planilha.location1") },
    { accessorKey: "LOCALIZACAO_2", header: t("planilha.location2") },
    { accessorKey: "LOCALIZACAO_3", header: t("planilha.location3") },
    { accessorKey: "LOCALIZACAO_4", header: t("planilha.location4") },
    ...indicesInfoCompl(impressaoConfig).map(
      (n): ColumnDef<PlanilhaItem> => ({
        accessorKey: `INF_COMPL_${n}`,
        header: t("planilha.infoCompl", { n }),
      }),
    ),
  ];

  const optionalColumns: ColumnDef<PlanilhaItem>[] = [];
  if (
    planilhaDeItens.some((item) => item.TAG !== undefined && item.TAG !== "")
  ) {
    optionalColumns.push({
      accessorKey: "TAG",
      header: "TAG",
      cell: ({ row }) => (
        <span className="text-xs">{row.original.TAG}</span>
      ),
    });
  }

  const columns: ColumnDef<PlanilhaItem>[] = [...optionalColumns, ...baseColumns];

  // ---- CSV parsing ----
  function removerAcentos(texto: string): string {
    return texto.normalize("NFD").replace(/[̀-ͯ]/g, "");
  }

  function normalizarCabecalho(texto: string): string {
    return removerAcentos(texto.trim())
      .replace(/['"]/g, "")
      .trim()
      .replace(/\s+/g, "_")
      .toUpperCase();
  }

  function pareceEpc(valor: string): boolean {
    return /^[0-9A-Fa-f]{16,24}$/.test(valor.trim().replace(/['"]/g, ""));
  }

  /**
   * Campos que a montagem do EPC consome da planilha.
   *
   * Quando a linha nao traz TAG, o EPC e montado aqui: no padrao personalizado
   * os blocos sao concatenados, entao um {info3} vazio nao da erro, so encurta
   * o EPC e o padStart(24, "0") completa com zero, gerando EPC trocado. Por
   * isso esses campos passam a ser exigidos junto com EAN e DESCRICAO.
   */
  function camposExigidosPelaMontagemDoEpc(): Array<keyof PlanilhaItem> {
    if (impressaoConfig?.gravacao_reversa) return [];

    const padrao = coletarPadraoEpc();

    if (padrao === "personalizado") {
      const campos = new Set<keyof PlanilhaItem>();
      for (const bloco of coletarBlocosEPC()) {
        const campo = CAMPO_DA_PLANILHA_POR_BLOCO_EPC[bloco.id];
        if (campo) campos.add(campo);
      }
      return Array.from(campos);
    }

    if (padrao === "gs1 gtin-13") return ["EAN"];

    return [];
  }

  function validarPlanilha(
    arr: any,
    primeiraLinhaDeDados: number,
  ): arr is PlanilhaItem[] {
    if (!Array.isArray(arr)) {
      alert.error(t("planilha.notArray"));
      return false;
    }

    const camposDoEpc = camposExigidosPelaMontagemDoEpc();
    const linhasPorCampo = new Map<string, number[]>();
    const camposFaltandoPeloEpc = new Set<string>();

    for (let i = 0; i < arr.length; i++) {
      const item = arr[i];
      const numeroDaLinha = i + primeiraLinhaDeDados;

      if (typeof item !== "object" || item === null) {
        alert.error(t("planilha.rowNotObject", { n: numeroDaLinha }));
        setPiscarBotaoExemplo(true);
        return false;
      }
      if (typeof item.TAG !== "undefined" && typeof item.TAG !== "string") {
        alert.error(t("planilha.tagMustBeString", { n: numeroDaLinha }));
        setPiscarBotaoExemplo(true);
        return false;
      }

      // Linha que ja traz o EPC pronto nao depende dos campos da montagem.
      const temTag = typeof item.TAG === "string" && item.TAG.trim() !== "";
      const obrigatorios = temTag
        ? CAMPOS_SEMPRE_OBRIGATORIOS
        : Array.from(new Set([...CAMPOS_SEMPRE_OBRIGATORIOS, ...camposDoEpc]));

      for (const campo of obrigatorios) {
        const valor = item[campo];
        if (typeof valor !== "string" || valor.trim() === "") {
          const linhas = linhasPorCampo.get(campo) ?? [];
          linhas.push(numeroDaLinha);
          linhasPorCampo.set(campo, linhas);
          if (!CAMPOS_SEMPRE_OBRIGATORIOS.includes(campo)) {
            camposFaltandoPeloEpc.add(campo);
          }
        }
      }
    }

    if (linhasPorCampo.size === 0) return true;

    // Todos os campos que faltaram numa mensagem so, com as linhas de cada um.
    const detalhes = Array.from(linhasPorCampo.entries()).map(
      ([campo, linhas]) => {
        const mostradas = linhas.slice(0, MAX_LINHAS_POR_CAMPO_NO_ERRO);
        const restantes = linhas.length - mostradas.length;
        const lista =
          mostradas.join(", ") +
          (restantes > 0 ? ` ${t("planilha.andMore", { n: restantes })}` : "");
        return t("planilha.fieldOnLines", { field: campo, lines: lista });
      },
    );

    alert.error(
      t("planilha.requiredFieldsMissing", { details: detalhes.join("; ") }) +
        (camposFaltandoPeloEpc.size > 0
          ? ` ${t("planilha.requiredByEpc", {
              fields: Array.from(camposFaltandoPeloEpc).join(", "),
            })}`
          : ""),
    );
    setPiscarBotaoExemplo(true);
    return false;
  }

  function csvParaJson(csv: string): {
    itens: PlanilhaItem[];
    cabecalhoAssumido: string[] | null;
  } {
    const vazio = { itens: [] as PlanilhaItem[], cabecalhoAssumido: null };

    const linhas = csv
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    if (linhas.length === 0) return vazio;

    const primeiraLinha = linhas[0].split(";").map(normalizarCabecalho);
    const temCabecalho = primeiraLinha.some((coluna) =>
      COLUNAS_CONHECIDAS.includes(coluna),
    );

    let cabecalhos: string[];
    let linhasDeDados: string[];
    let cabecalhoAssumido: string[] | null = null;

    if (temCabecalho) {
      cabecalhos = primeiraLinha;
      linhasDeDados = linhas.slice(1);
    } else {
      // Nenhuma coluna conhecida na primeira linha: o arquivo nao tem
      // cabecalho. Assume a ordem do modelo oficial e avisa na tela.
      const primeiroValor = linhas[0].split(";")[0] ?? "";
      cabecalhos = pareceEpc(primeiroValor)
        ? COLUNAS_MODELO_COM_EPC
        : COLUNAS_MODELO_SEM_EPC;
      linhasDeDados = linhas;
      cabecalhoAssumido = cabecalhos.slice(0, primeiraLinha.length);
    }

    if (linhasDeDados.length === 0) return vazio;

    const resultado = linhasDeDados.map((linha) => {
      const valores = linha
        .split(";")
        .map((v) => removerAcentos(v.trim()).replace(/['"]/g, ""));
      // Coluna ausente no arquivo entra vazia, em vez de reprovar a planilha.
      const obj: Record<string, string> = {};
      for (const coluna of COLUNAS_MODELO_COM_EPC) obj[coluna] = "";
      cabecalhos.forEach((cab, idx) => {
        if (COLUNAS_CONHECIDAS.includes(cab)) obj[cab] = valores[idx] ?? "";
      });
      return obj;
    });

    return validarPlanilha(resultado, temCabecalho ? 2 : 1)
      ? { itens: resultado, cabecalhoAssumido }
      : vazio;
  }

  // ---- Processamento do arquivo selecionado ----
  const processarArquivo = (file: File) => {
    if (!file.name.toLowerCase().endsWith(".csv")) {
      alert.error(t("planilha.onlyCsv"));
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      const csvData = event.target?.result as string;
      const { itens: planilhaTratada, cabecalhoAssumido } =
        csvParaJson(csvData);
      if (planilhaTratada.length > 0) {
        const newPlanilhaTratadaEpcCom24Chars = planilhaTratada.map((item) => {
          if (item.TAG && item.TAG !== "") {
            return { ...item, TAG: item.TAG.padStart(24, "0") };
          }
          return item;
        });
        setPlanilhaDeItens(newPlanilhaTratadaEpcCom24Chars);
        setCabecalhoAssumido(cabecalhoAssumido);
        setFileName(file.name);

        // Detectar conversões logo na importação pra exibir o aviso
        const conversoes: Array<{ original: string; convertido: string }> = [];
        for (const item of planilhaTratada) {
          const eanOriginal = item.EAN || "";
          const eanHash = sanitizarCodigoHex(eanOriginal);
          if (
            eanHash !== eanOriginal.toUpperCase() &&
            !conversoes.some((c) => c.original === eanOriginal)
          ) {
            conversoes.push({ original: eanOriginal, convertido: eanHash });
          }
        }
        setCodigosConvertidos(conversoes);
        alert.success(t("planilha.importSuccess"));
      }
    };
    reader.readAsText(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      processarArquivo(e.target.files[0]);
    } else {
      alert.error(t("planilha.noFileSelected"));
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (planilhaDeItens.length > 0 || isLoading) return;
    const file = e.dataTransfer.files?.[0];
    if (file) processarArquivo(file);
  };

  const limparPlanilha = () => {
    setPlanilhaDeItens([]);
    setCodigosConvertidos([]);
    setCabecalhoAssumido(null);
    setFileName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ---- Sincronizar ----
  const sincronizar = async () => {
    await coletaCategorias();
    await coletaSeriais();
    await coletaLocalizacoes();
    setPlanilhaDeItens([]);
    alert.success(t("planilha.syncSuccess"));
  };

  // ---- Gerar lista pra impressão ----
  const gerarListaParaImpressao = async () => {
    if (!planilhaDeItens.length) {
      alert.error(t("planilha.noItemsToPrint"));
      return;
    }
    if (!impressaoConfig) {
      alert.error(t("planilha.configNotFound"));
      return;
    }
    if (!impressaoConfig?.app_id) {
      alert.error(t("planilha.informHardwareId"));
      return;
    }

    if (impressaoConfig.gravacao_reversa) {
      if (planilhaDeItens.some((item) => item.TAG && item.TAG !== "")) {
        alert.error(t("planilha.reverseWithTags"));
        return;
      }
    }

    setIsLoading(true);

    try {
      const categoriasFormatadas: NewCategoriaItem[] = [];
      const conversoes: Array<{ original: string; convertido: string }> = [];
      for (let i = 0; i < planilhaDeItens.length; i++) {
        const eanOriginal = planilhaDeItens[i].EAN || "";
        const eanHash = sanitizarCodigoHex(eanOriginal);
        if (
          eanHash !== eanOriginal.toUpperCase() &&
          !conversoes.some((c) => c.original === eanOriginal)
        ) {
          conversoes.push({ original: eanOriginal, convertido: eanHash });
        }
        // Coluna vazia na planilha cai na pré-definição do campo quando ela tem
        // uma única opção cadastrada (não há o que escolher).
        const categoria: NewCategoriaItem = aplicarPreDefinicoesUnicas(
          impressaoConfig,
          {
            descricao: planilhaDeItens[i].DESCRICAO || "",
            ean: eanHash,
            id_conta: impressaoConfig.id_conta,
            inf_compl_1: planilhaDeItens[i].INF_COMPL_1 || "",
            inf_compl_2: planilhaDeItens[i].INF_COMPL_2 || "",
            inf_compl_3: planilhaDeItens[i].INF_COMPL_3 || "",
            inf_compl_4: planilhaDeItens[i].INF_COMPL_4 || "",
            inf_compl_5: planilhaDeItens[i].INF_COMPL_5 || "",
            inf_compl_6: planilhaDeItens[i].INF_COMPL_6 || "",
            inf_compl_7: planilhaDeItens[i].INF_COMPL_7 || "",
            inf_compl_8: planilhaDeItens[i].INF_COMPL_8 || "",
            inf_compl_9: planilhaDeItens[i].INF_COMPL_9 || "",
            inf_compl_10: planilhaDeItens[i].INF_COMPL_10 || "",
            inf_compl_11: infoExtraDaPlanilha(planilhaDeItens[i].INF_COMPL_11),
            inf_compl_12: infoExtraDaPlanilha(planilhaDeItens[i].INF_COMPL_12),
          },
        );
        if (!categoriasFormatadas.some((c) => c.ean === categoria.ean)) {
          categoriasFormatadas.push(categoria);
        }
      }
      setCodigosConvertidos(conversoes);

      let categoriasCriadas: CategoriaItemResponse[] = [];
      const categoriasParaCriar: NewCategoriaItem[] = [];
      for (const categoria of categoriasFormatadas) {
        const categoriaExistente = pesquisarCategoria(categoria.ean);
        const categoriaCriada = categoriasCriadas.find(
          (cat) => cat.ean === categoria.ean,
        );
        if (
          !categoriaExistente &&
          !categoriaCriada &&
          !categoriasParaCriar.some((c) => c.ean === categoria.ean)
        ) {
          categoriasParaCriar.push(categoria);
        } else {
          if (categoriaExistente) {
            if (
              categoria.descricao.trim() !== "" &&
              categoriaExistente.descricao.trim() !== categoria.descricao.trim()
            ) {
              await atualizarDescricaoCategoria(
                categoria.ean,
                categoria.descricao,
              );
            }
            categoriasCriadas.push({
              ...categoriaExistente,
              inf_compl_1: categoria.inf_compl_1,
              inf_compl_2: categoria.inf_compl_2,
              inf_compl_3: categoria.inf_compl_3,
              inf_compl_4: categoria.inf_compl_4,
              inf_compl_5: categoria.inf_compl_5,
              inf_compl_6: categoria.inf_compl_6,
              inf_compl_7: categoria.inf_compl_7,
              inf_compl_8: categoria.inf_compl_8,
              inf_compl_9: categoria.inf_compl_9,
              inf_compl_10: categoria.inf_compl_10,
              inf_compl_11: categoria.inf_compl_11,
              inf_compl_12: categoria.inf_compl_12,
              descricao: categoria.descricao,
            });
          } else if (categoriaCriada) {
            categoriasCriadas.push(categoriaCriada);
          } else {
            alert.error(
              t("planilha.categoryNotFoundBase", { ean: categoria.ean }),
            );
            return;
          }
        }
      }

      const categoriasParaCriarCriadas =
        await criarCategorias(categoriasParaCriar);
      if (categoriasParaCriarCriadas) {
        categoriasCriadas = [...categoriasCriadas, ...categoriasParaCriarCriadas];
      }

      const categoriasECategoriasCriadas: CategoriaItemResponse[] = [
        ...categoriasCriadas,
      ];

      let localizacoesNv1Formatadas: newLocalizacaoNv1[] = [];
      let localizacoesNv2Formatadas: newLocalizacaoNv2[] = [];
      let localizacoesNv3Formatadas: newLocalizacaoNv3[] = [];
      let localizacoesNv4Formatadas: newLocalizacaoNv4[] = [];

      for (let i = 0; i < planilhaDeItens.length; i++) {
        if (!planilhaDeItens[i].LOCALIZACAO_1) {
          alert.error(t("planilha.location1Missing", { n: i + 1 }));
          return;
        }
        const localizacaoNv1: newLocalizacaoNv1 = {
          ativo: 1,
          cor: "",
          descricao: planilhaDeItens[i].LOCALIZACAO_1,
          id_conta: impressaoConfig.id_conta,
          loc_baixados: false,
          observacao: "",
          tag: "",
        };
        if (
          !localizacoesNv1Formatadas.some(
            (loc) => loc.descricao === localizacaoNv1.descricao,
          )
        ) {
          localizacoesNv1Formatadas.push(localizacaoNv1);
        }
        if (!planilhaDeItens[i].LOCALIZACAO_2) continue;
        const localizacaoNv2: newLocalizacaoNv2 = {
          ativo: 1,
          cor: "",
          descricao: planilhaDeItens[i].LOCALIZACAO_2,
          id_conta: impressaoConfig.id_conta,
          loc_baixados: false,
          observacao: "",
          tag: "",
          id_nivel_loc1: localizacaoNv1.descricao,
        };

        if (
          !localizacoesNv2Formatadas.some(
            (loc) => loc.descricao === localizacaoNv2.descricao,
          )
        ) {
          localizacoesNv2Formatadas.push(localizacaoNv2);
        }

        if (!planilhaDeItens[i].LOCALIZACAO_3) continue;

        const localizacaoNv3: newLocalizacaoNv3 = {
          ativo: 1,
          cor: "",
          descricao: planilhaDeItens[i].LOCALIZACAO_3,
          id_conta: impressaoConfig.id_conta,
          loc_baixados: false,
          observacao: "",
          tag: "",
          id_nivel_loc2: localizacaoNv2.descricao,
        };

        if (
          !localizacoesNv3Formatadas.some(
            (loc) => loc.descricao === localizacaoNv3.descricao,
          )
        ) {
          localizacoesNv3Formatadas.push(localizacaoNv3);
        }

        if (!planilhaDeItens[i].LOCALIZACAO_4) continue;

        const localizacaoNv4: newLocalizacaoNv4 = {
          ativo: 1,
          cor: "",
          descricao: planilhaDeItens[i].LOCALIZACAO_4,
          id_conta: impressaoConfig.id_conta,
          loc_baixados: false,
          observacao: "",
          tag: "",
          id_nivel_loc3: localizacaoNv3.descricao,
        };

        if (
          !localizacoesNv4Formatadas.some(
            (loc) => loc.descricao === localizacaoNv4.descricao,
          )
        ) {
          localizacoesNv4Formatadas.push(localizacaoNv4);
        }
      }

      const localizacoesNv1Criadas: LocalizacaoItem[] = [];
      for (const localizacao of localizacoesNv1Formatadas) {
        const localizacaoExistente = localizacoes.find(
          (loc) => loc.descricao === localizacao.descricao,
        );
        const jaAdicionada = localizacoesNv1Criadas.some(
          (loc) => loc.descricao === localizacao.descricao,
        );
        if (!localizacaoExistente && !jaAdicionada) {
          const novaLocalizacao = await criarLocalizacoesNV1([localizacao]);
          if (novaLocalizacao && novaLocalizacao.length > 0) {
            localizacoesNv1Criadas.push(novaLocalizacao[0]);
          }
        }
        if (localizacaoExistente && !jaAdicionada) {
          localizacoesNv1Criadas.push(localizacaoExistente);
        }
      }

      if (localizacoesNv1Criadas) {
        const novasNv2: newLocalizacaoNv2[] = [];
        for (const loc of localizacoesNv2Formatadas) {
          const localizacaoExistente = localizacoesNv1Criadas.find(
            (l) => l.descricao === loc.id_nivel_loc1,
          );
          if (!localizacaoExistente) {
            alert.error(t("planilha.nv2NotLinked", { desc: loc.descricao }));
            return;
          }
          novasNv2.push({ ...loc, id_nivel_loc1: localizacaoExistente._id });
        }
        localizacoesNv2Formatadas = novasNv2;
      }

      const localizacoesNv2Criadas: LocalizacaoNv2Item[] = [];
      for (const localizacao of localizacoesNv2Formatadas) {
        const localizacaoExistente = localizacoes
          .flatMap((loc) => loc.children)
          .find(
            (loc2) =>
              loc2.id_nivel_loc1 === localizacao.id_nivel_loc1 &&
              loc2.descricao === localizacao.descricao,
          );

        const jaAdicionada = localizacoesNv2Criadas.some(
          (loc) => loc.descricao === localizacao.descricao,
        );

        if (
          !localizacaoExistente &&
          localizacao.id_nivel_loc1 !== "" &&
          !jaAdicionada
        ) {
          const novaLocalizacao = await criarLocalizacoesNV2([localizacao]);
          if (novaLocalizacao && novaLocalizacao.length > 0) {
            localizacoesNv2Criadas.push(novaLocalizacao[0]);
          }
        }
        if (localizacaoExistente && !jaAdicionada) {
          localizacoesNv2Criadas.push(localizacaoExistente);
        }
      }

      if (localizacoesNv2Criadas) {
        const novasNv3: newLocalizacaoNv3[] = [];
        for (const loc of localizacoesNv3Formatadas) {
          const localizacaoExistente = localizacoesNv2Criadas.find(
            (l) => l.descricao === loc.id_nivel_loc2,
          );
          if (!localizacaoExistente) {
            alert.error(t("planilha.nv3NotLinked", { desc: loc.descricao }));
            return;
          }
          novasNv3.push({ ...loc, id_nivel_loc2: localizacaoExistente._id });
        }
        localizacoesNv3Formatadas = novasNv3;
      }

      const localizacoesNv3Criadas: LocalizacaoNv3Item[] = [];
      for (const localizacao of localizacoesNv3Formatadas) {
        const localizacaoExistente = localizacoes
          .flatMap((loc) => loc.children)
          .flatMap((loc2) => loc2.children)
          .find(
            (loc3) =>
              loc3.id_nivel_loc2 === localizacao.id_nivel_loc2 &&
              loc3.descricao === localizacao.descricao,
          );

        const jaAdicionada = localizacoesNv3Criadas.some(
          (loc) => loc.descricao === localizacao.descricao,
        );

        if (
          !localizacaoExistente &&
          localizacao.id_nivel_loc2 !== "" &&
          !jaAdicionada
        ) {
          const novaLocalizacao = await criarLocalizacoesNV3([localizacao]);
          if (novaLocalizacao && novaLocalizacao.length > 0) {
            localizacoesNv3Criadas.push(novaLocalizacao[0]);
          }
        }
        if (localizacaoExistente && !jaAdicionada) {
          localizacoesNv3Criadas.push(localizacaoExistente);
        }
      }

      if (localizacoesNv3Criadas) {
        const novasNv4: newLocalizacaoNv4[] = [];
        for (const loc of localizacoesNv4Formatadas) {
          const localizacaoExistente = localizacoesNv3Criadas.find(
            (l) => l.descricao === loc.id_nivel_loc3,
          );
          if (!localizacaoExistente) {
            alert.error(t("planilha.nv4NotLinked", { desc: loc.descricao }));
            return;
          }
          novasNv4.push({ ...loc, id_nivel_loc3: localizacaoExistente._id });
        }
        localizacoesNv4Formatadas = novasNv4;
      }

      const localizacoesNv4Criadas: LocalizacaoNv4Item[] = [];
      for (const localizacao of localizacoesNv4Formatadas) {
        const localizacaoExistente = localizacoes
          .flatMap((loc) => loc.children)
          .flatMap((loc2) => loc2.children)
          .flatMap((loc3) => loc3.children)
          .find(
            (loc4) =>
              loc4.id_nivel_loc3 === localizacao.id_nivel_loc3 &&
              loc4.descricao === localizacao.descricao,
          );

        const jaAdicionada = localizacoesNv4Criadas.some(
          (loc) => loc.descricao === localizacao.descricao,
        );

        if (
          !localizacaoExistente &&
          localizacao.id_nivel_loc3 !== "" &&
          !jaAdicionada
        ) {
          const novaLocalizacao = await criarLocalizacoesNV4([localizacao]);
          if (novaLocalizacao && novaLocalizacao.length > 0) {
            localizacoesNv4Criadas.push(novaLocalizacao[0]);
          }
        }
        if (localizacaoExistente && !jaAdicionada) {
          localizacoesNv4Criadas.push(localizacaoExistente);
        }
      }

      const listaParaImpressao: ImpressaoItem[] = [];
      const serialPorEan: Record<string, number> = {};
      let numero_de_impressoes = numeroDeImpressoes || 0;

      for (let i = 0; i < planilhaDeItens.length; i++) {
        const item = planilhaDeItens[i];

        const categoria = categoriasECategoriasCriadas.find(
          (cat) => cat.ean === sanitizarCodigoHex(item.EAN),
        );
        if (!categoria) {
          alert.error(t("planilha.categoryNotFound", { ean: item.EAN }));
          continue;
        }

        let epc = "";
        let zpl_impressao = "";
        let _id = "";

        const eanHash = sanitizarCodigoHex(item.EAN);
        let serialAtual = serialPorEan[eanHash];
        if (serialAtual === undefined) {
          serialAtual =
            (seriais.find((s) => s.gtin === eanHash)?.serial ?? -1) + 1;
        }
        const localizacoesZpl: string[] = [
          item.LOCALIZACAO_1 || "",
          item.LOCALIZACAO_2 || "",
          item.LOCALIZACAO_3 || "",
          item.LOCALIZACAO_4 || "",
        ];
        const epcGerado = gerarEpcs(
          {
            _id: categoria._id,
            descricao: item.DESCRICAO,
            ean: categoria.ean,
            id_conta: categoria.id_conta,
            inf_compl_1: item.INF_COMPL_1,
            inf_compl_2: item.INF_COMPL_2,
            inf_compl_3: item.INF_COMPL_3,
            inf_compl_4: item.INF_COMPL_4,
            inf_compl_5: item.INF_COMPL_5,
            inf_compl_6: item.INF_COMPL_6,
            inf_compl_7: item.INF_COMPL_7,
            inf_compl_8: item.INF_COMPL_8,
            inf_compl_9: item.INF_COMPL_9,
            inf_compl_10: item.INF_COMPL_10,
            inf_compl_11:
              infoExtraDaPlanilha(item.INF_COMPL_11) || categoria.inf_compl_11,
            inf_compl_12:
              infoExtraDaPlanilha(item.INF_COMPL_12) || categoria.inf_compl_12,
          },
          1,
          localizacoesZpl,
          coletarBlocosEPC(),
          serialAtual,
        )[0];

        if (!epcGerado) {
          alert.error(
            `Não foi possível gerar o EPC do produto ${item.EAN}${
              item.DESCRICAO ? ` - ${item.DESCRICAO}` : ""
            }. Ajuste os dados desse item na planilha e tente novamente.`,
          );
          return;
        }

        if (item.TAG && item.TAG !== "") {
          epc = item.TAG.padStart(24, "0");
          zpl_impressao = epcGerado.zpl_impressao_bo
            .split(epcGerado.epc)
            .join(epc)
            .split(epcGerado.epc.slice(-6))
            .join(epc.slice(-6))
            .split(epcGerado.epc.replace(/^0+/, ""))
            .join(epc.replace(/^0+/, ""));
        } else {
          epc = epcGerado.epc;
          zpl_impressao = epcGerado.zpl_impressao_bo;
        }
        _id = epcGerado._id;

        serialPorEan[eanHash] = serialAtual + 1;

        listaParaImpressao.push({
          _id,
          inf_compl_1: verificarVariaveisDeSubstituicaoEmInfosComplementares(
            epcGerado,
            item.INF_COMPL_1,
          ),
          inf_compl_2: verificarVariaveisDeSubstituicaoEmInfosComplementares(
            epcGerado,
            item.INF_COMPL_2,
          ),
          inf_compl_3: verificarVariaveisDeSubstituicaoEmInfosComplementares(
            epcGerado,
            item.INF_COMPL_3,
          ),
          inf_compl_4: verificarVariaveisDeSubstituicaoEmInfosComplementares(
            epcGerado,
            item.INF_COMPL_4,
          ),
          inf_compl_5: verificarVariaveisDeSubstituicaoEmInfosComplementares(
            epcGerado,
            item.INF_COMPL_5,
          ),
          inf_compl_6: verificarVariaveisDeSubstituicaoEmInfosComplementares(
            epcGerado,
            item.INF_COMPL_6,
          ),
          inf_compl_7: verificarVariaveisDeSubstituicaoEmInfosComplementares(
            epcGerado,
            item.INF_COMPL_7,
          ),
          inf_compl_8: verificarVariaveisDeSubstituicaoEmInfosComplementares(
            epcGerado,
            item.INF_COMPL_8,
          ),
          inf_compl_9: verificarVariaveisDeSubstituicaoEmInfosComplementares(
            epcGerado,
            item.INF_COMPL_9,
          ),
          inf_compl_10: verificarVariaveisDeSubstituicaoEmInfosComplementares(
            epcGerado,
            item.INF_COMPL_10,
          ),
          descricao: item.DESCRICAO,
          codigo: sanitizarCodigoHex(item.EAN),
          inf_compl_11: verificarVariaveisDeSubstituicaoEmInfosComplementares(
            epcGerado,
            infoExtraDaPlanilha(item.INF_COMPL_11),
          ),
          inf_compl_12: verificarVariaveisDeSubstituicaoEmInfosComplementares(
            epcGerado,
            infoExtraDaPlanilha(item.INF_COMPL_12),
          ),
          tag: epc,
          zpl_impressao: zpl_impressao,
          id_categoria: categoria._id,
          id_conta: impressaoConfig.id_conta,
          id_usuario: impressaoConfig.id_usuario,
          id_localizacao1:
            localizacoesNv1Criadas.find(
              (l) => l.descricao === item.LOCALIZACAO_1,
            )?._id || "",
          id_localizacao2: (() => {
            const idLoc1 =
              localizacoesNv1Criadas.find(
                (l) => l.descricao === item.LOCALIZACAO_1,
              )?._id || "";
            return (
              localizacoesNv2Criadas.find(
                (l) =>
                  l.descricao === item.LOCALIZACAO_2 &&
                  l.id_nivel_loc1 === idLoc1,
              )?._id || ""
            );
          })(),
          id_localizacao3: (() => {
            const idLoc1 =
              localizacoesNv1Criadas.find(
                (l) => l.descricao === item.LOCALIZACAO_1,
              )?._id || "";
            const idLoc2 =
              localizacoesNv2Criadas.find(
                (l) =>
                  l.descricao === item.LOCALIZACAO_2 &&
                  l.id_nivel_loc1 === idLoc1,
              )?._id || "";
            return (
              localizacoesNv3Criadas.find(
                (l) =>
                  l.descricao === item.LOCALIZACAO_3 &&
                  l.id_nivel_loc2 === idLoc2,
              )?._id || ""
            );
          })(),
          id_localizacao4: (() => {
            const idLoc1 =
              localizacoesNv1Criadas.find(
                (l) => l.descricao === item.LOCALIZACAO_1,
              )?._id || "";
            const idLoc2 =
              localizacoesNv2Criadas.find(
                (l) =>
                  l.descricao === item.LOCALIZACAO_2 &&
                  l.id_nivel_loc1 === idLoc1,
              )?._id || "";
            const idLoc3 =
              localizacoesNv3Criadas.find(
                (l) =>
                  l.descricao === item.LOCALIZACAO_3 &&
                  l.id_nivel_loc2 === idLoc2,
              )?._id || "";
            return (
              localizacoesNv4Criadas.find(
                (l) =>
                  l.descricao === item.LOCALIZACAO_4 &&
                  l.id_nivel_loc3 === idLoc3,
              )?._id || ""
            );
          })(),
          valor: Number(item.VALOR) || 0,
          quantidade: item.QUANTIDADE || "",
          dt_validade: brDateToISO(item.DT_VALIDADE) || "",
          n_impressao: numero_de_impressoes.toString(),
          id_externo: "",
          ultima_localizacao: {
            primeira_vez: true,
            verificar: contaConfig?.transferencia_loc_origem || false,
          },
        });
        numero_de_impressoes++;
      }

      if (listaParaImpressao.length === 0) {
        alert.error(t("planilha.noEpcGenerated"));
        return;
      }
      if (!permitirItensRepetidos) {
        const itensNaBase = await conferirTagsCadastradas(
          listaParaImpressao.map((item) => item.tag),
        );
        if (itensNaBase.length > 0) {
          const repetidosFormatados = listaParaImpressao
            .filter((item) => itensNaBase.some((i) => i.tag === item.tag))
            .map((item) => ({ epc: item.tag, codigo: item.codigo }));
          setEpcRepetidosEncontrados(repetidosFormatados);
          setEpcRepetidosDialog(true);
          adicionarMaisUmNosSeriais(
            repetidosFormatados.map((item) => item.codigo),
          );
          return;
        }
      }
      const epcs_para_impressao = listaParaImpressao.map((item) => item.tag);
      if (verificarEpcsInvalidos(epcs_para_impressao)) {
        alert.error(t("planilha.invalidEpcs"));
        return;
      }
      adicionarItensNaFila(
        listaParaImpressao,
        undefined,
        undefined,
        MODOS_IMPRESSAO.PLANILHA,
      );
      setPlanilhaDeItens([]);
      setCodigosConvertidos([]);
      setFileName("");
    } catch (error) {
      console.error("Erro ao gerar lista para impressão:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold tracking-tight">
            {t("planilha.title")}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t("planilha.subtitle")}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 rounded-md border bg-card px-3 h-9">
            <Switch
              id="planilha-repetidos"
              disabled={isLoading}
              checked={permitirItensRepetidos}
              onCheckedChange={(checked) => {
                requireAdmin(() => {
                  setPermitirItensRepetidos(checked);
                  localStorage.setItem(
                    "permitirItensRepetidos",
                    String(checked),
                  );
                });
              }}
            />
            <Label
              htmlFor="planilha-repetidos"
              className="text-xs cursor-pointer"
            >
              {t("sscc.allowRepeated")}
            </Label>
          </div>

          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={piscarBotaoExemplo ? "destructive" : "outline"}
                size="sm"
                className={piscarBotaoExemplo ? "animate-caret-blink" : ""}
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">{t("planilha.examples")}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-2">
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground px-2 pb-1">
                  {t("planilha.downloadModel")}
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start text-xs"
                  onClick={() =>
                    window.open(link_exemplo_planilha_item_sem_epc, "_blank")
                  }
                >
                  <FileSpreadsheet className="h-3.5 w-3.5" />
                  {t("planilha.items")}{" "}
                  <span className="text-muted-foreground">
                    {t("planilha.withoutTag")}
                  </span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start text-xs"
                  onClick={() =>
                    window.open(link_exemplo_planilha_item_com_epc, "_blank")
                  }
                >
                  <FileSpreadsheet className="h-3.5 w-3.5" />
                  {t("planilha.items")}{" "}
                  <span className="text-muted-foreground">
                    {t("planilha.withTag")}
                  </span>
                </Button>
              </div>
            </PopoverContent>
          </Popover>

          <Button
            variant="outline"
            size="sm"
            onClick={sincronizar}
            disabled={isLoading}
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
            />
            <span className="hidden sm:inline">{t("common2.sync")}</span>
          </Button>

          <Button
            size="sm"
            onClick={gerarListaParaImpressao}
            disabled={planilhaDeItens.length === 0 || isLoading}
            title={
              planilhaDeItens.length === 0
                ? t("planilha.importToPrint")
                : t("planilha.printNLabels", { count: planilhaDeItens.length })
            }
          >
            <Printer className="h-4 w-4" />
            <span className="hidden sm:inline">{t("common2.print")}</span>
            {planilhaDeItens.length > 0 && (
              <Badge
                variant="secondary"
                className="ml-1 h-5 px-1.5 text-[10px] bg-white/20 text-primary-foreground hover:bg-white/20"
              >
                {planilhaDeItens.length}
              </Badge>
            )}
          </Button>
        </div>
      </div>

      {/* 1. Importar arquivo */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
              1
            </div>
            <CardTitle className="text-sm font-medium flex items-center gap-1.5">
              <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
              {t("planilha.importCsvFile")}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {planilhaDeItens.length === 0 ? (
            <div
              onDragOver={(e) => {
                e.preventDefault();
                if (!isLoading) setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`flex flex-col items-center justify-center gap-3 rounded-md border-2 border-dashed py-10 px-4 cursor-pointer transition-colors ${
                isDragging
                  ? "border-primary bg-primary/5"
                  : "border-border bg-muted/20 hover:bg-muted/40 hover:border-primary/40"
              } ${isLoading ? "pointer-events-none opacity-60" : ""}`}
            >
              <div
                className={`flex h-12 w-12 items-center justify-center rounded-full transition-colors ${
                  isDragging ? "bg-primary/15" : "bg-muted"
                }`}
              >
                <Upload
                  className={`h-6 w-6 ${
                    isDragging ? "text-primary" : "text-muted-foreground"
                  }`}
                />
              </div>
              <div className="text-center space-y-1">
                <p className="text-sm font-medium">
                  {isDragging
                    ? t("planilha.dropHere")
                    : t("planilha.dragOrClick")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("planilha.acceptsOnly")}{" "}
                  <code className="px-1 py-0.5 rounded bg-muted text-[10px]">
                    .csv
                  </code>{" "}
                  {t("planilha.useExamples")}
                </p>
              </div>
              <input
                ref={fileInputRef}
                id="importar-planilha-file"
                type="file"
                accept=".csv"
                className="sr-only"
                onChange={handleFileInputChange}
                disabled={isLoading}
              />
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/30 p-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500/10">
                  <FileSpreadsheet className="h-5 w-5 text-emerald-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">
                    {fileName || t("planilha.fileLoaded")}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t("planilha.itemImported", {
                      count: planilhaDeItens.length,
                    })}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={limparPlanilha}
                disabled={isLoading}
              >
                <X className="h-4 w-4" />
                {t("planilha.clear")}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Aviso de planilha sem linha de cabeçalho */}
      {cabecalhoAssumido && cabecalhoAssumido.length > 0 && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="p-3 flex items-start gap-2 text-xs">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="flex flex-col gap-1 text-amber-700 dark:text-amber-300 min-w-0">
              <span className="font-medium">{t("planilha.headerAssumed")}</span>
              <span className="opacity-80">
                {t("planilha.headerAssumedDesc")}
              </span>
              <span className="mt-1 font-mono break-all">
                {cabecalhoAssumido.join(";")}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Aviso de conversão de códigos não-hex */}
      {codigosConvertidos.length > 0 && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="p-3 flex items-start gap-2 text-xs">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="flex flex-col gap-1 text-amber-700 dark:text-amber-300 min-w-0">
              <span className="font-medium">
                {t("planilha.codesConverted", {
                  count: codigosConvertidos.length,
                })}
              </span>
              <span className="opacity-80">
                {t("planilha.codesConvertedDesc")}
              </span>
              <ul className="mt-1 list-disc list-inside space-y-0.5 max-h-24 overflow-y-auto">
                {codigosConvertidos.map(({ original, convertido }) => (
                  <li key={original}>
                    <span className="">{original}</span>
                    <span className="mx-1">→</span>
                    <span className="font-medium">{convertido}</span>
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 2. Itens da planilha */}
      {planilhaDeItens.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
                  2
                </div>
                <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                  <Layers className="h-4 w-4 text-muted-foreground" />
                  {t("planilha.sheetItems")}
                </CardTitle>
              </div>
              <Badge variant="secondary" className="font-medium">
                {t("planilha.itemsCount", { count: planilhaDeItens.length })}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <DataTable columns={columns} data={planilhaDeItens} />
          </CardContent>
        </Card>
      )}

      {/* AlertDialog: EPCs repetidos */}
      <AlertDialog
        open={epcRepetidosDialog}
        onOpenChange={setEpcRepetidosDialog}
      >
        <AlertDialogContent className="max-h-[90vh] overflow-hidden flex flex-col">
          <AlertDialogHeader>
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-500/10">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              </div>
              <AlertDialogTitle>
                {t("planilha.repeatedEpcsFound")}
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription>
              {t("planilha.repeatedEpcsDesc")}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="overflow-y-auto max-h-64 rounded-md border">
            <ul className="divide-y">
              {epcRepetidosEncontrados.map((item, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between px-3 py-2 gap-2"
                >
                  <span className="text-xs truncate">{item.epc}</span>
                  <Badge variant="secondary" className="">
                    {item.codigo}
                  </Badge>
                </li>
              ))}
            </ul>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>{t("common2.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => setEpcRepetidosDialog(false)}>
              <Eye className="h-4 w-4" />
              {t("common2.understood")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default ImportarPlanilha;
